import { Cartesian2, Cartesian3 } from "cesium";
import * as ClothoidUtils from "../../geometric-elements/clothoid-utils";
import { EarthBrainUtils } from "../../../utilities/earthbrainutility";
import { closestPointOnLine, perpendicularDistanceFromPointToLine, } from "../../../utilities/math-utils";
//----------------------------constants-------------------------------
export const getDeflectionAngleInDegrees = (p1, p2, p3) => {
    const unit1 = EarthBrainUtils.getNormalizedDirection(p2, p1);
    const unit2 = EarthBrainUtils.getNormalizedDirection(p2, p3);
    // Calculate the deflection angle (0 is straight, 180 is a U-turn)
    const dot = Cartesian3.dot(unit1, unit2);
    const angleRadians = Math.acos(Math.max(-1, Math.min(1, dot)));
    const deflectionDegrees = 180 - (angleRadians * 180) / Math.PI;
    return deflectionDegrees;
};
export const getFrictionFactor = (speedKmH) => {
    if (speedKmH <= 30)
        return 0.4;
    if (speedKmH <= 50)
        return 0.2;
    if (speedKmH <= 80)
        return 0.14;
    if (speedKmH <= 100)
        return 0.12;
    return 0.1; // High speed default
};
export const calculateHACurveParameters = (speedKmH) => {
    const v = speedKmH / 3.6; // m/s
    const e = 0.06; // 6% bank
    const fs = getFrictionFactor(speedKmH);
    // 1. Calculate Radius with a 'Safety Floor'
    // This prevents R from being so small that Ls explodes
    const R_calculated = Math.pow(speedKmH, 2) / (127 * (e + fs));
    const R = Math.max(R_calculated, 6.0); // 6m floor for vehicle wheelbase
    // 2. Calculate Spiral Length with Boundaries
    const C = 0.5; // Comfort factor
    let Ls = Math.pow(v, 3) / (R * C);
    // Apply AASHTO-style limits
    const minLs = 0.7 * speedKmH; // Appearance limit
    const maxLs = R; // Practical limit: Spiral shouldn't be longer than the radius
    Ls = Math.max(Ls, minLs);
    Ls = Math.min(Ls, maxLs);
    const S = Math.pow(Ls, 2) / (24 * R);
    // 3. Calculate Tangent Distance
    // Note: delta comes from your vector math (start, pi, end)
    return { R, Ls, S }; // radius, length of spiral, shift
};
/**
 * Computes the world coordinates for a road curve.
 */
export function generateCurve(start, pi, end, speedKmH) {
    // 1. Vector Math Setup
    const v1 = Cartesian3.subtract(start, pi, new Cartesian3());
    const v2 = Cartesian3.subtract(end, pi, new Cartesian3());
    const unit1 = Cartesian3.normalize(v1, new Cartesian3());
    const unit2 = Cartesian3.normalize(v2, new Cartesian3());
    // 2. Calculate Intersection Angle (Delta)
    const dot = unit1.x * unit2.x + unit1.y * unit2.y + unit1.z * unit2.z;
    const delta = Math.PI - Math.acos(Math.max(-1, Math.min(1, dot)));
    // 3. Physical Parameters
    const { R, Ls, S } = calculateHACurveParameters(speedKmH);
    const parameterA = Math.sqrt(R * Ls);
    const Ts = (R + S) * Math.tan(delta / 2) + Ls / 2;
    // 4. Locate TS and ST in World Space
    const tsPoint = Cartesian3.add(pi, Cartesian3.multiplyByScalar(unit1, Ts, new Cartesian3()), new Cartesian3());
    const stPoint = Cartesian3.add(pi, Cartesian3.multiplyByScalar(unit2, Ts, new Cartesian3()), new Cartesian3());
    // 5. Calculate Center of Arc
    // The center lies along the bisector of the angle at distance (R + S) / cos(delta/2) from PI
    const bisector = Cartesian3.normalize(Cartesian3.add(unit1, unit2, new Cartesian3()), new Cartesian3());
    const distToCenter = (R + S) / Math.cos(delta / 2);
    const centerPoint = Cartesian3.add(pi, Cartesian3.multiplyByScalar(bisector, distToCenter, new Cartesian3()), new Cartesian3());
    // 6. Generate Sampled Points (Local to World transformation)
    const spiralToCurve = ClothoidUtils.getSpiralPointAtLengthUsingParameterA(Ls, parameterA, Cartesian3.negate(unit1, new Cartesian3()), ClothoidUtils.getOrthogonalDirection(Cartesian3.negate(unit1, new Cartesian3()), centerPoint, tsPoint), tsPoint);
    const curveToSpiral = ClothoidUtils.getSpiralPointAtLengthUsingParameterA(Ls, parameterA, Cartesian3.negate(unit2, new Cartesian3()), ClothoidUtils.getOrthogonalDirection(Cartesian3.negate(unit2, new Cartesian3()), centerPoint, stPoint), stPoint);
    return {
        tangentToSpiral: tsPoint,
        spiralToCurve,
        center: centerPoint,
        curveToSpiral,
        spiralToTangent: stPoint,
        lengthOfSpiral: Ls,
        parameterA,
    };
}
export const getClosestPointInfo = (point, horizontalAlignment) => {
    const getIndexForProbableSegment = (point, pathpoints) => {
        const perpDistances = [];
        for (let i = 1; i < pathpoints.length; i++) {
            const perpDistance = perpendicularDistanceFromPointToLine(point, pathpoints[i - 1], pathpoints[i], true);
            perpDistances.push(perpDistance);
        }
        const indexOfminDistance = perpDistances.indexOf(Math.min(...perpDistances));
        return indexOfminDistance;
    };
    const points = horizontalAlignment.points;
    const subSegmentIndex = getIndexForProbableSegment(point, points);
    const result = closestPointOnLine(point, points[subSegmentIndex], points[subSegmentIndex + 1], true);
    let distance = 0;
    for (let i = 0; i < subSegmentIndex; ++i) {
        distance += Cartesian2.distance(points[i], points[i + 1]);
    }
    distance += Cartesian2.distance(points[subSegmentIndex], result);
    return {
        point: result,
        distanceFromStart: distance,
    };
};
export const getInfoAtDistanceInBatch = (sortedDistanceFromStart, horizontalAlignment) => {
    const distances = [...sortedDistanceFromStart];
    const result = [];
    const points = horizontalAlignment.points;
    let cumulativeDistance = 0;
    let prevPoint;
    let pointOnPath;
    for (let i = 0; i < points.length; i++) {
        const pathPoint = points[i];
        if (prevPoint) {
            const distance = Cartesian2.distance(prevPoint, pathPoint);
            if (distance === 0) {
                continue;
            }
            cumulativeDistance += distance;
            // multiple distances can be with in two points
            while (distances.length > 0 && cumulativeDistance >= distances[0]) {
                const direction = EarthBrainUtils.getDirBetweenTwoCartesians(new Cartesian3(prevPoint.x, prevPoint.y, 0), new Cartesian3(pathPoint.x, pathPoint.y, 0));
                pointOnPath = Cartesian3.add(prevPoint, Cartesian3.multiplyByScalar(direction, distance - cumulativeDistance + distances[0], new Cartesian3()), new Cartesian3());
                result.push(pointOnPath);
                distances.shift();
            }
        }
        prevPoint = pathPoint;
    }
    return result;
};
export const getPointInfoAtDistance = (distanceFromStart, horizontalAlignment) => {
    const points = horizontalAlignment.points;
    if (distanceFromStart >= horizontalAlignment.length) {
        return Cartesian3.clone(points[points.length - 1], new Cartesian3());
    }
    let cumulativeDistance = 0;
    let prevPoint;
    let pointOnPath;
    for (let i = 0; i < points.length; i++) {
        const pathPoint = points[i];
        if (prevPoint) {
            const distance = Cartesian2.distance(prevPoint, pathPoint);
            if (distance === 0) {
                continue;
            }
            cumulativeDistance += distance;
            if (cumulativeDistance >= distanceFromStart) {
                const direction = EarthBrainUtils.getDirBetweenTwoCartesians(new Cartesian3(prevPoint.x, prevPoint.y, 0), new Cartesian3(pathPoint.x, pathPoint.y, 0));
                pointOnPath = Cartesian3.add(prevPoint, Cartesian3.multiplyByScalar(direction, distance - cumulativeDistance + distanceFromStart, new Cartesian3()), new Cartesian3());
                break;
            }
        }
        prevPoint = pathPoint;
    }
    return pointOnPath;
};
export const getDistanceFromStart = (point, horizontalAlignment) => {
    let distance = 0;
    const points = horizontalAlignment.points;
    for (let i = 0; i < points.length - 1; i++) {
        if (points[i].equals(point)) {
            return distance;
        }
        distance += Cartesian2.distance(points[i], points[i + 1]);
    }
    if (points[points.length - 1].equals(point)) {
        return distance;
    }
    // If point not found exactly, try to find closest point on segments ?
    // For now, assuming point is one of the vertices.
    // If strictly needed for any point on path, we need projection.
    // Given the usage in Alignment.points map, we serve exactly the vertices.
    return -1;
};
//# sourceMappingURL=horizontal-alignment-utils.js.map